import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.className === 'purchase-checkbox') return;
    const selectedNonPlayerOptions = this.getSelectedNonPlayerOptions();
    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;
    const selectedPlayerSwitcherValue = this.getSelectedPlayerSwitcherValue();
    const selectedVariantId = this.getVariantIdFromTarget(event.target, selectedOption, selectedPlayerSwitcherValue);

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? selectedVariantId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const playerConnectedProductUrl = this.getConnectedProductUrlFromPlayerValue(selectedPlayerSwitcherValue);
    const effectiveConnectedProductUrl = playerConnectedProductUrl || selectedOption.dataset.connectedProductUrl;

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = effectiveConnectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption, null, [], effectiveConnectedProductUrl),
      loadsNewProduct,
      selectedOption,
      selectedPlayerSwitcherValue,
      selectedNonPlayerOptions,
      selectedVariantId
    );

    const url = new URL(window.location.href);
    const variantId = selectedVariantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], connectedProductUrlOverride = null) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      connectedProductUrlOverride ||
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    // On PDP, use Section Rendering API so we only fetch this section instead of the full page (avoids slow full-page fetch+parse on every variant change)
    const sectionId = this.dataset.sectionId;
    if (sectionId && productUrl) {
      const baseUrl = productUrl.split('?')[0];
      const query = params.length ? `section_id=${encodeURIComponent(sectionId)}&${params.join('&')}` : `section_id=${encodeURIComponent(sectionId)}`;
      return `${baseUrl}?${query}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Returns the currently selected player product URL if available.
   * This prevents non-player option changes (e.g. size) from jumping
   * back to a default player product in combined listing flows.
   * @returns {string | undefined}
   */
  getSelectedPlayerSwitcherValue() {
    const playerSwitcher = this.querySelector('select[data-player-switcher="true"]');
    if (!(playerSwitcher instanceof HTMLSelectElement)) return '';

    const rawValue = playerSwitcher.value?.trim();
    return rawValue || '';
  }

  /**
   * Converts a player switcher value into a connected product URL when possible.
   * @param {string} rawValue
   * @returns {string | undefined}
   */
  getConnectedProductUrlFromPlayerValue(rawValue) {
    if (!rawValue) return undefined;

    // Player switchers can hold either a product URL (combined listing)
    // or a variant id (single product). Only URL values should override.
    if (/^\d+$/.test(rawValue)) return undefined;

    try {
      if (/^https?:\/\//i.test(rawValue)) {
        return new URL(rawValue).pathname;
      }
      if (rawValue.startsWith('/')) return rawValue;
    } catch (error) {
      return undefined;
    }

    return undefined;
  }

  /**
   * Resolves variant id from selected target.
   * Supports player switchers that use plain numeric option values.
   * @param {HTMLElement} target
   * @param {HTMLElement | undefined} selectedOption
   * @param {string} selectedPlayerSwitcherValue
   * @returns {string}
   */
  getVariantIdFromTarget(target, selectedOption, selectedPlayerSwitcherValue = '') {
    const fromSelectedOption = selectedOption?.dataset?.variantId;
    if (fromSelectedOption) return fromSelectedOption;

    const fromCurrentSelected = this.selectedOption?.dataset?.variantId;
    if (fromCurrentSelected) return fromCurrentSelected;

    if (target instanceof HTMLSelectElement && target.matches('select[data-player-switcher="true"]')) {
      const numericValue = selectedPlayerSwitcherValue.trim();
      if (/^\d+$/.test(numericValue)) {
        return numericValue;
      }
    }

    return '';
  }

  /**
   * Re-applies player switcher selection after variant picker morph.
   * @param {string} selectedValue
   */
  restorePlayerSwitcherSelection(selectedValue) {
    if (!selectedValue) return;

    const switchers = this.querySelectorAll('select[data-player-switcher="true"]');
    switchers.forEach((switcher) => {
      if (!(switcher instanceof HTMLSelectElement)) return;
      const matchingOption = Array.from(switcher.options).find((option) => option.value === selectedValue);
      if (!matchingOption) return;

      switcher.value = selectedValue;
      Array.from(switcher.options).forEach((option) => option.removeAttribute('selected'));
      matchingOption.setAttribute('selected', 'selected');
    });
  }

  /**
   * Captures currently selected non-player options (e.g. size/color) by human-readable value.
   * This allows re-applying choices even when switching between combined-listing products
   * where option value IDs can differ across products.
   * @returns {{ type: 'radio' | 'select', optionKey: string, value: string }[]}
   */
  getSelectedNonPlayerOptions() {
    /** @type {{ type: 'radio' | 'select', optionKey: string, value: string }[]} */
    const selections = [];

    const checkedRadios = this.querySelectorAll('input[type="radio"]:checked');
    checkedRadios.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.value) return;
      const optionKey = (input.className || input.name || '').trim();
      if (!optionKey) return;
      selections.push({ type: 'radio', optionKey, value: input.value });
    });

    const selects = this.querySelectorAll('select:not([data-player-switcher="true"])');
    selects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;
      if (!select.value) return;
      const optionKey = (select.name || '').trim();
      if (!optionKey) return;
      selections.push({ type: 'select', optionKey, value: select.value });
    });

    return selections;
  }

  /**
   * Re-applies non-player selections after morph.
   * @param {{ type: 'radio' | 'select', optionKey: string, value: string }[]} selections
   * @returns {HTMLInputElement | HTMLSelectElement | null}
   */
  restoreNonPlayerSelections(selections = []) {
    if (!Array.isArray(selections) || selections.length === 0) return null;

    /** @type {HTMLInputElement | HTMLSelectElement | null} */
    let changedTarget = null;

    selections.forEach((selection) => {
      if (!selection?.optionKey || !selection?.value) return;

      if (selection.type === 'radio') {
        const radios = this.querySelectorAll('input[type="radio"]');
        const target = Array.from(radios).find((radio) => {
          if (!(radio instanceof HTMLInputElement)) return false;
          const classes = (radio.className || '').split(/\s+/).filter(Boolean);
          return classes.includes(selection.optionKey) && radio.value === selection.value && !radio.disabled;
        });
        if (!(target instanceof HTMLInputElement)) return;
        if (target.checked) return;

        target.checked = true;
        changedTarget = changedTarget || target;
        return;
      }

      if (selection.type === 'select') {
        const select = this.querySelector(`select[name="${selection.optionKey}"]:not([data-player-switcher="true"])`);
        if (!(select instanceof HTMLSelectElement)) return;
        if (select.value === selection.value) return;

        const targetOption = Array.from(select.options).find((option) => option.value === selection.value);
        if (!targetOption) return;

        select.value = selection.value;
        Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
        targetOption.setAttribute('selected', 'selected');
        changedTarget = changedTarget || select;
      }
    });

    return changedTarget;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   * @param {HTMLElement} [selectedOption] - The selected option element.
   */
  fetchUpdatedSection(
    requestUrl,
    shouldMorphMain = false,
    selectedOption = undefined,
    selectedPlayerSwitcherValue = '',
    selectedNonPlayerOptions = [],
    selectedVariantId = ''
  ) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        // Try different selectors to find the variant data
        let textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        
        if (!textContent) {
          // Try alternative selectors
          textContent = html.querySelector(`script[type="application/json"]`)?.textContent;
        }
        
        if (!textContent) {
          // Try looking in the main content
          textContent = html.querySelector(`main script[type="application/json"]`)?.textContent;
        }
        
        // Simplified approach: always use the selected option data
        const fallbackOption = selectedOption || this.selectedOption;
        
        if (shouldMorphMain) {
          this.updateMain(html);
          this.restorePlayerSwitcherSelection(selectedPlayerSwitcherValue);
          const restoredTarget = this.restoreNonPlayerSelections(selectedNonPlayerOptions);
          if (restoredTarget) {
            restoredTarget.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        } else {
          const newProduct = this.updateVariantPicker(html);
          this.restorePlayerSwitcherSelection(selectedPlayerSwitcherValue);
          const restoredTarget = this.restoreNonPlayerSelections(selectedNonPlayerOptions);
          if (restoredTarget) {
            restoredTarget.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }

          // We grab the variant object from the response and dispatch an event with it.
          const selectedOption = this.selectedOption;
          const variantId = selectedVariantId || selectedOption?.dataset.variantId || '';

          if (variantId) {
            const isAvailable = selectedOption ? selectedOption.dataset.optionAvailable !== 'false' : true;
            
            const variantData = {
              id: variantId,
              available: isAvailable,
              inventory_management: false
            };
            
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, variantId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]')
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
